import React, { useState, useEffect, useCallback } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CButton,
  CFormInput,
  CFormSelect,
  CInputGroup,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CSpinner,
} from "@coreui/react-pro";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import {
  getPolicyReconciliation,
  getPolicyReconciliationSummary,
  exportPolicyReconciliation,
} from "../../../utils/backend";
import RecordRefundModal from "./RecordRefundModal";

const MYR = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : new Intl.NumberFormat("en-MY", {
        style: "currency",
        currency: "MYR",
        minimumFractionDigits: 2,
      }).format(Number(v));

const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10);
};

const paymentBadgeColor = (s) =>
  s === "Completed"
    ? "success"
    : s === "Refunded"
    ? "danger"
    : s === "Partially Refunded"
    ? "info"
    : "warning";

const hasPermission = (group, name) => {
  let permissions = [];
  try {
    permissions = JSON.parse(localStorage.getItem("permissions")) || [];
  } catch (e) {
    permissions = [];
  }
  return permissions.some(
    (p) => p.permission_group === group && p.permission_name === name
  );
};

const EMPTY = {
  startDate: "",
  endDate: "",
  channel: "cermat",
  purchasedBy: "all",
  insurerName: "",
  orderNumber: "",
  vehicleNumber: "",
  fullName: "",
  paymentStatus: "",
};

const PolicyReconciliation = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState(EMPTY);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [refundRow, setRefundRow] = useState(null);

  const buildFilters = useCallback(
    () => ({
      start_date: filters.startDate,
      end_date: filters.endDate,
      channel: filters.channel,
      purchased_by: filters.purchasedBy,
      insurer_name: filters.insurerName,
      order_number: filters.orderNumber,
      vehicle_number: filters.vehicleNumber,
      full_name: filters.fullName,
      payment_status: filters.paymentStatus,
      insurance_type: 5,
      paid_only: true,
    }),
    [filters]
  );

  const fetchData = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const payload = { filters: buildFilters(), page, perPage };
        const [listRes, sumRes] = await Promise.all([
          getPolicyReconciliation(payload),
          getPolicyReconciliationSummary({ filters: buildFilters() }),
        ]);
        setRows(listRes.data.data || []);
        setCurrentPage(listRes.data.currentPage || 1);
        setTotalPages(listRes.data.totalPages || 1);
        setTotalItems(listRes.data.totalItems || 0);
        setSummary(sumRes.data || null);
      } catch (error) {
        console.error("Error fetching report:", error);
        toast.error(error.message || "Failed to load report");
        setRows([]);
        setSummary(null);
      } finally {
        setIsLoading(false);
      }
    },
    [buildFilters, perPage]
  );

  // Debounced search whenever filters or page size change.
  useEffect(() => {
    const t = setTimeout(() => fetchData(1), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, perPage]);

  const setField = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.value }));

  const handleReset = () => setFilters(EMPTY);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportPolicyReconciliation({ filters: buildFilters() });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cermat_Madani_Policy_Cost_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const card = (label, value, sub, color = "primary") => (
    <CCol sm={6} xl={3} className="mb-3">
      <CCard className="h-100 border-light">
        <CCardBody>
          <div className="text-medium-emphasis small text-uppercase">{label}</div>
          <div className={`fs-4 fw-semibold text-${color}`}>{value}</div>
          {sub ? <div className="small text-medium-emphasis">{sub}</div> : null}
        </CCardBody>
      </CCard>
    </CCol>
  );

  return (
    <>
      <CCard className="border-light mb-3">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <strong>Policy Cost &amp; Payment Reconciliation — Cermat Madani</strong>
          {hasPermission("Finance Report Management", "Export") && (
            <CButton
              color="success"
              className="text-white"
              onClick={handleExport}
              disabled={isExporting || isLoading}
            >
              {isExporting ? (
                <CSpinner size="sm" />
              ) : (
                <>
                  <i className="bi bi-file-earmark-excel me-2"></i>Export to Excel
                </>
              )}
            </CButton>
          )}
        </CCardHeader>
        <CCardBody>
          {/* Summary cards */}
          <CRow>
            {card(
              "Total Paid Policies",
              summary ? summary.total_policies : "—",
              summary ? (
                <>
                  {MYR(summary.total_amount)} gross
                  {summary.refunded_amount ? (
                    <>
                      {" · "}
                      <span className="text-success">
                        Net {MYR(summary.net_received)}
                      </span>{" "}
                      ({summary.refunded_policies} refunded)
                    </>
                  ) : null}
                </>
              ) : (
                ""
              )
            )}
            {card(
              "Customer-paid",
              summary ? summary.customer_policies : "—",
              summary ? MYR(summary.customer_amount) : "",
              "info"
            )}
            {card(
              "Agent / Staff-paid",
              summary ? summary.staff_policies : "—",
              summary ? `${MYR(summary.staff_amount)} · vs RHB top-up` : "",
              "warning"
            )}
            {card(
              "Flags to review",
              summary
                ? summary.flags.paid_but_pending +
                  summary.flags.paid_no_cover_note +
                  summary.flags.charged_vs_quoted_gap +
                  summary.flags.test_probe
                : "—",
              summary
                ? `${summary.flags.paid_no_cover_note} no cover note · ${summary.flags.paid_but_pending} paid-pending`
                : "",
              "danger"
            )}
          </CRow>

          {/* Filters */}
          <CRow className="g-2 mb-3 align-items-end">
            <CCol md={2}>
              <label className="small text-medium-emphasis">From</label>
              <CFormInput
                type="date"
                value={filters.startDate}
                onChange={setField("startDate")}
              />
            </CCol>
            <CCol md={2}>
              <label className="small text-medium-emphasis">To</label>
              <CFormInput
                type="date"
                value={filters.endDate}
                onChange={setField("endDate")}
              />
            </CCol>
            <CCol md={2}>
              <label className="small text-medium-emphasis">Channel</label>
              <CFormSelect value={filters.channel} onChange={setField("channel")}>
                <option value="all">All channels</option>
                <option value="cermat">Cermat Madani (portal)</option>
                <option value="direct">Direct (app)</option>
              </CFormSelect>
            </CCol>
            <CCol md={2}>
              <label className="small text-medium-emphasis">Purchased by</label>
              <CFormSelect value={filters.purchasedBy} onChange={setField("purchasedBy")}>
                <option value="all">All</option>
                <option value="customer">Customer</option>
                <option value="staff">Agent / Staff</option>
              </CFormSelect>
            </CCol>
            <CCol md={2}>
              <label className="small text-medium-emphasis">Insurer</label>
              <CFormSelect value={filters.insurerName} onChange={setField("insurerName")}>
                <option value="">All insurers</option>
                <option value="Generali Insurance Berhad">Generali</option>
                <option value="RHB Insurance Berhad">RHB</option>
                <option value="Tokio Marine Insurance (Malaysia) Berhad">Tokio Marine</option>
                <option value="Takaful Ikhlas General Berhad">Takaful Ikhlas</option>
                <option value="Liberty General Insurance Berhad">Liberty</option>
                <option value="Berjaya Sompo Insurance Berhad">Berjaya Sompo</option>
                <option value="Etiqa General Insurance Berhad">Etiqa</option>
                <option value="Tune Insurance Malaysia Berhad">Tune Protect</option>
                <option value="Pacific & Orient Insurance Co. Berhad">Pacific &amp; Orient</option>
                <option value="Lonpac Insurance Berhad">Lonpac</option>
                <option value="Allianz General Insurance Company (Malaysia) Berhad">Allianz</option>
              </CFormSelect>
            </CCol>
            <CCol md={2}>
              <label className="small text-medium-emphasis">Payment status</label>
              <CFormSelect
                value={filters.paymentStatus}
                onChange={setField("paymentStatus")}
              >
                <option value="">All</option>
                <option value="Completed">Completed</option>
                <option value="Pending">Pending</option>
                <option value="Refunded">Refunded</option>
                <option value="Partially Refunded">Partially Refunded</option>
              </CFormSelect>
            </CCol>
            <CCol md={2}>
              <label className="small text-medium-emphasis">Plate</label>
              <CFormInput
                placeholder="Search plate"
                value={filters.vehicleNumber}
                onChange={setField("vehicleNumber")}
              />
            </CCol>
            <CCol md={2} className="d-flex gap-2">
              <CFormInput
                placeholder="Order no."
                value={filters.orderNumber}
                onChange={setField("orderNumber")}
              />
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleReset}
                title="Reset filters"
              >
                Reset
              </CButton>
            </CCol>
          </CRow>

          {/* Table */}
          {isLoading ? (
            <Skeleton height={32} count={8} />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <CTable borderless striped hover>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>#</CTableHeaderCell>
                    <CTableHeaderCell>Plate</CTableHeaderCell>
                    <CTableHeaderCell>Issued date</CTableHeaderCell>
                    <CTableHeaderCell>Order Number</CTableHeaderCell>
                    <CTableHeaderCell>Customer</CTableHeaderCell>
                    <CTableHeaderCell>Insurer</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Amount Paid</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">Rebate gap</CTableHeaderCell>
                    <CTableHeaderCell>Purchased By</CTableHeaderCell>
                    <CTableHeaderCell>Account Type</CTableHeaderCell>
                    <CTableHeaderCell>Payment</CTableHeaderCell>
                    <CTableHeaderCell>Cover Note</CTableHeaderCell>
                    <CTableHeaderCell>Flags</CTableHeaderCell>
                    <CTableHeaderCell>Actions</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {rows.length === 0 ? (
                    <CTableRow>
                      <CTableDataCell colSpan={14} className="text-center text-medium-emphasis py-4">
                        No paid policies match the selected filters.
                      </CTableDataCell>
                    </CTableRow>
                  ) : (
                    rows.map((r, i) => (
                      <CTableRow key={r.id}>
                        <CTableDataCell>{i + 1 + perPage * (currentPage - 1)}</CTableDataCell>
                        <CTableDataCell>{r.car_plate || "-"}</CTableDataCell>
                        <CTableDataCell>{fmtDate(r.policy_issued_date)}</CTableDataCell>
                        <CTableDataCell>{r.order_number || "-"}</CTableDataCell>
                        <CTableDataCell>{r.customer_name || "-"}</CTableDataCell>
                        <CTableDataCell>{r.insurer_name || "-"}</CTableDataCell>
                        <CTableDataCell className="text-end">{MYR(r.amount_paid)}</CTableDataCell>
                        <CTableDataCell className="text-end">
                          {r.rebate_gap ? MYR(r.rebate_gap) : "-"}
                        </CTableDataCell>
                        <CTableDataCell>
                          <CBadge
                            color={r.purchased_by === "Agent / Staff" ? "warning" : "secondary"}
                          >
                            {r.purchased_by}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell>
                          <CBadge
                            color={
                              r.account_type && r.account_type.startsWith("Cermat")
                                ? "info"
                                : "secondary"
                            }
                          >
                            {r.account_type}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell>
                          <CBadge color={paymentBadgeColor(r.payment_status)}>
                            {r.payment_status || "-"}
                          </CBadge>
                          {(r.payment_status === "Refunded" ||
                            r.payment_status === "Partially Refunded") && (
                            <div className="small text-medium-emphasis">
                              −{MYR(r.refund_amount)} · net {MYR(r.net_received)}
                            </div>
                          )}
                        </CTableDataCell>
                        <CTableDataCell>{r.cover_note_no || "—"}</CTableDataCell>
                        <CTableDataCell style={{ minWidth: 220 }}>
                          {r.reconciliation_flags ? (
                            <span className="text-danger small">{r.reconciliation_flags}</span>
                          ) : (
                            <span className="text-success small">OK</span>
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {hasPermission("Refund Management", "Record") &&
                          r.payment_status === "Completed" ? (
                            <CButton
                              size="sm"
                              color="danger"
                              variant="outline"
                              onClick={() => setRefundRow(r)}
                            >
                              Record refund
                            </CButton>
                          ) : null}
                        </CTableDataCell>
                      </CTableRow>
                    ))
                  )}
                </CTableBody>
              </CTable>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <CPagination aria-label="Page navigation">
                <CPaginationItem
                  disabled={currentPage <= 1}
                  onClick={() => fetchData(currentPage - 1)}
                >
                  &laquo;
                </CPaginationItem>
                {[...Array(totalPages).keys()]
                  .filter(
                    (p) =>
                      p === 0 ||
                      p === totalPages - 1 ||
                      (p >= currentPage - 3 && p <= currentPage + 1)
                  )
                  .map((p) => (
                    <CPaginationItem
                      key={p + 1}
                      active={currentPage === p + 1}
                      onClick={() => fetchData(p + 1)}
                    >
                      {p + 1}
                    </CPaginationItem>
                  ))}
                <CPaginationItem
                  disabled={currentPage >= totalPages}
                  onClick={() => fetchData(currentPage + 1)}
                >
                  &raquo;
                </CPaginationItem>
              </CPagination>
              <div className="d-flex align-items-center">
                <span className="me-3 small text-medium-emphasis">
                  Showing {rows.length} of {totalItems} policies
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <RecordRefundModal
        row={refundRow}
        onClose={() => setRefundRow(null)}
        onSuccess={() => fetchData(currentPage)}
      />
    </>
  );
};

export default PolicyReconciliation;
